import { useState } from "react";

import Card from "../../../components/ui/Card";
import Button from "../../../components/ui/Button";
import { LynxPackField } from "./LynxPackField";
import { AgencyPicker } from "./AgencyPicker";
import { createOrder } from "../api";
import { CARRIER_LABEL, carrierDe, LastQuote, OrderDetail, RateResult } from "../types";
import { PROVINCIAS } from "../provincias";

type RecipientFormProps = {
  quote: LastQuote;
  rate: RateResult;
  onCreated: (order: OrderDetail) => void;
  onCancel: () => void;
};

const selectStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid var(--border)",
  background: "var(--surface-alt)",
  color: "var(--text-primary)",
  fontSize: 14,
};

function formatPrice(price: number | string) {
  return typeof price === "number" ? `$ ${price.toLocaleString("es-AR")}` : String(price);
}

// El paso 2 del flujo: quién recibe. A domicilio pide la dirección (el CP es
// el cotizado y no se puede cambiar: el precio es para ese destino); a
// sucursal alcanza con los datos personales más la sucursal de retiro.
// El precio y el bulto NO viajan: los congela el servidor desde la
// cotización guardada.
export function RecipientForm({ quote, rate, onCreated, onCancel }: RecipientFormProps) {
  const aDomicilio = rate.deliveredType === "D";
  const carrier = carrierDe(rate);
  // Andreani exige el documento del destinatario (DNI o CUIT/CUIL).
  const pideDocumento = carrier === "andreani";

  // El valor declarado es el de la cotización: el precio ya lo incluye (en
  // Andreani, el seguro). Otro valor es otro precio y hay que cotizarlo de
  // nuevo. Solo una cotización sin valor (de antes) lo pide acá.
  const declaradoCotizado = quote.declaredValue;
  const [declaredValue, setDeclaredValue] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [street, setStreet] = useState("");
  const [number, setNumber] = useState("");
  const [floor, setFloor] = useState("");
  const [apartment, setApartment] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [agencyId, setAgencyId] = useState("");
  const [documentType, setDocumentType] = useState<"DNI" | "CUIT" | "CUIL">("DNI");
  const [documentNumber, setDocumentNumber] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const completo =
    (declaradoCotizado !== undefined || Number(declaredValue) > 0) &&
    name.trim() &&
    phone.trim() &&
    email.trim() &&
    (!pideDocumento || documentNumber.trim()) &&
    (aDomicilio
      ? street.trim() && number.trim() && city.trim() && province.trim()
      : agencyId.trim());

  const handleCreate = async () => {
    setError("");
    setSaving(true);

    try {
      const orden = await createOrder({
        quoteId: Number(quote.id),
        carrier,
        productType: String(rate.productType),
        deliveredType: rate.deliveredType as "D" | "S",
        declaredValue: declaradoCotizado ?? Number(declaredValue),
        agencyId: aDomicilio ? undefined : agencyId,
        recipient: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          ...(pideDocumento ? { document: { type: documentType, number: documentNumber.trim() } } : {}),
          ...(aDomicilio
            ? {
                address: {
                  street: street.trim(),
                  number: number.trim(),
                  floor: floor.trim() || undefined,
                  apartment: apartment.trim() || undefined,
                  city: city.trim(),
                  province: province.trim(),
                  postalCode: quote.postalCodeDestination,
                },
              }
            : {}),
        },
      });

      onCreated(orden);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo crear la orden.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: "var(--text-primary)", fontSize: 20 }}>
          Destinatario
        </h2>
        <p style={{ margin: "6px 0 0", color: "var(--text-secondary)", fontSize: 13 }}>
          {CARRIER_LABEL[carrier]} · {rate.service} · {aDomicilio ? "a domicilio" : "retiro en sucursal"} ·{" "}
          {formatPrice(rate.price)} · cotización #{quote.id} ({quote.postalCodeOrigin} →{" "}
          {quote.postalCodeDestination})
        </p>
      </div>

      <div style={{ display: "grid", gap: 14 }}>
        {declaradoCotizado !== undefined ? (
          <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 13 }}>
            Valor declarado:{" "}
            <strong style={{ color: "var(--text-primary)" }}>{formatPrice(declaradoCotizado)}</strong> — es
            el cotizado; para otro valor hay que cotizar de nuevo.
          </p>
        ) : (
          <div>
            <LynxPackField
              label="Valor declarado: precio del contenido ($)"
              value={declaredValue}
              onChange={setDeclaredValue}
              type="number"
              placeholder="Precio real de la mercadería"
            />
            <p style={{ margin: "4px 0 0", color: "var(--text-secondary)", fontSize: 12 }}>
              Es la base de la indemnización del transportista ante pérdida o daño: declarar el
              valor real del contenido, el que respalda la factura de venta.
            </p>
          </div>
        )}

        <LynxPackField label="Nombre y apellido" value={name} onChange={setName} placeholder="Ana Pérez" />

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
          <LynxPackField label="Teléfono" value={phone} onChange={setPhone} placeholder="1155550000" />
          <LynxPackField label="Email" value={email} onChange={setEmail} placeholder="ana@correo.com" />
        </div>

        {pideDocumento && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 14 }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>Documento</span>
              <select value={documentType} onChange={(e) => setDocumentType(e.target.value as "DNI" | "CUIT" | "CUIL")} style={selectStyle}>
                <option value="DNI">DNI</option>
                <option value="CUIT">CUIT</option>
                <option value="CUIL">CUIL</option>
              </select>
            </label>
            <LynxPackField
              label={`Número (Andreani lo exige)`}
              value={documentNumber}
              onChange={setDocumentNumber}
              placeholder={documentType === "DNI" ? "30123456" : "20301234567"}
            />
          </div>
        )}

        {aDomicilio ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14 }}>
              <LynxPackField label="Calle" value={street} onChange={setStreet} placeholder="Av. Siempreviva" />
              <LynxPackField label="Altura" value={number} onChange={setNumber} placeholder="742" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
              <LynxPackField label="Piso (opcional)" value={floor} onChange={setFloor} placeholder="1" />
              <LynxPackField label="Depto (opcional)" value={apartment} onChange={setApartment} placeholder="B" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 14 }}>
              <LynxPackField label="Localidad" value={city} onChange={setCity} placeholder="Rosario" />

              {/* Elegir, no escribir: Correo traduce la provincia a un código
                  de una letra y rechaza lo que no reconoce. Escrita a mano, un
                  "Bs As" pasaba el alta, se cobraba el envío y recién fallaba
                  al despachar, con la orden ya paga y sin forma de corregirla. */}
              <label style={{ display: "grid", gap: 6 }}>
                <span style={{ color: "var(--text-secondary)", fontSize: 13, fontWeight: 600 }}>
                  Provincia
                </span>
                <select
                  value={province}
                  onChange={(event) => setProvince(event.target.value)}
                  style={selectStyle}
                >
                  <option value="">Elegí una provincia</option>
                  {PROVINCIAS.map((provincia) => (
                    <option key={provincia.codigo} value={provincia.nombre}>
                      {provincia.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <p style={{ margin: 0, color: "var(--text-secondary)", fontSize: 12 }}>
              CP de entrega: <strong>{quote.postalCodeDestination}</strong> — es el cotizado; para
              otro destino hay que cotizar de nuevo.
            </p>
          </>
        ) : (
          <AgencyPicker
            value={agencyId}
            onChange={setAgencyId}
            postalCode={quote.postalCodeDestination}
            carrier={carrier}
          />
        )}

        {error && (
          <div style={{ color: "var(--danger)", fontSize: 13, fontWeight: 600 }}>{error}</div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Button variant="secondary" fullWidth onClick={onCancel} disabled={saving}>
            Volver
          </Button>
          <Button variant="primary" fullWidth onClick={handleCreate} disabled={!completo || saving}>
            {saving ? "Creando orden..." : "Crear orden"}
          </Button>
        </div>
      </div>
    </Card>
  );
}
